import { createContext, useEffect, useState, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import Cookies from 'universal-cookie';
import { getPersonalizedProductsIds } from '@/api/Products.js';
import { useConfiguration } from '@/contexts/ConfigContext.jsx';

export const PersonalizedProductsIdsContext = createContext({
  personalizedProductIds: [],
});

export function PersonalizedProductsIdsProvider({ children }) {
  const { locale } = useRouter();
  const [personalizedProductsIds, setPersonalizedProductsIds] = useState([]);
  const cookies = new Cookies();
  const authorization = cookies.get('token');
  const { config: configuration } = useConfiguration();
  const personalizedProductsEnabled = configuration?.catalog?.product?.recommendations?.user?.enabled ?? false;

  const { data: fetchedPersonalizedProductsIds } = useQuery({
    queryKey: ['personalizedProductIds', locale],
    queryFn: getPersonalizedProductsIds,
    staleTime: 1000 * 60 * 60 * 12, // Cache the data for 12 hours
    enabled: !!authorization && personalizedProductsEnabled,
  });

  useEffect(() => {
    if (fetchedPersonalizedProductsIds) setPersonalizedProductsIds(fetchedPersonalizedProductsIds);
  }, [fetchedPersonalizedProductsIds]);

  // Clear state when authorization becomes undefined
  useEffect(() => {
    if (!authorization) {
      setPersonalizedProductsIds([]);
    }
  }, [authorization]);

  return (
    <PersonalizedProductsIdsContext.Provider value={{ personalizedProductsIds }}>
      {children}
    </PersonalizedProductsIdsContext.Provider>
  );
}

export function usePersonalizedProductsIds() {
  const context = useContext(PersonalizedProductsIdsContext);
  if (!context) {
    throw new Error('usePersonalizedProductsIds must be used within a PersonalizedProductsIdsProvider');
  }
  return context;
}
